import type { StudioMessage, StudioTurn } from '../api/types';
import { elapsedSeconds, useNow } from '../app/useNow';

export interface ThinkingLineProps {
  /** queued か running のターン。 */
  turn: StudioTurn;
  /** そのターンの tool_call の行（seq の昇順）。作業ログと同じ数を数える。 */
  toolCalls: StudioMessage[];
}

/**
 * 考えている間の手応え（U5）。経過時間は 1 秒ごとに数え直す（本文そのものは C27 の 1 秒更新で届く）。
 * 実行中のツールがあれば名前とそのツールの経過時間、そのターンでのツールの回数も出す。
 *
 * role="status" はライブリージョンなので、1 秒ごとに変わる部分（経過秒・ツール・回数）は aria-hidden にして
 * 読み上げの対象から外す（毎秒読み上げると、回答待ちの帯など本当に伝えたい通知が埋もれる）。
 */
export function ThinkingLine({ turn, toolCalls }: ThinkingLineProps) {
  const now = useNow(true);
  const seconds = elapsedSeconds(turn.startedAt ?? turn.createdAt, now);
  // 実行中の行は普通 1 つだけ。並んだときは新しい方（seq が大きい方）を出す。
  const tool = [...toolCalls].reverse().find((entry) => entry.body['status'] === 'running') ?? null;
  const name = tool !== null && typeof tool.body['name'] === 'string' ? tool.body['name'] : '';
  return (
    <p className="studio-thinking" role="status">
      <span className="studio-thinking-dot" aria-hidden="true" />
      <span>{turn.status === 'queued' ? '順番を待っています…' : '考えています…'}</span>
      <span className="studio-thinking-seconds" aria-hidden="true">{` ${seconds} 秒`}</span>
      {name !== '' && tool !== null && (
        <span className="studio-thinking-tool" aria-hidden="true">
          {`${name} を実行中（${elapsedSeconds(tool.createdAt, now)} 秒）`}
        </span>
      )}
      {toolCalls.length > 0 && (
        <span className="studio-thinking-count" aria-hidden="true">{`操作 ${toolCalls.length} 回`}</span>
      )}
    </p>
  );
}
